import { useState, type ReactNode } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import MarketStrip from "./MarketStrip";
import ModeToggle from "./ModeToggle";
import StatusCenter from "./StatusCenter";
import AIThinkingDrawer from "./AIThinkingDrawer";

const NAV_ITEMS = [
  { to: "/", label: "Dashboard", icon: "▦" },
  { to: "/universe", label: "Universe", icon: "🔍" },
  { to: "/charts", label: "Charts", icon: "📈" },
  { to: "/ai-insights", label: "AI Insights", icon: "🤖" },
  { to: "/timemachine", label: "Time Machine", icon: "⏰" },
  { to: "/positions", label: "Positions", icon: "⬒" },
  { to: "/orders", label: "Orders", icon: "☰" },
  { to: "/trades", label: "Trades", icon: "↔" },
  { to: "/strategies", label: "Strategies", icon: "⚡" },
  { to: "/backtests", label: "Backtests", icon: "▶" },
  { to: "/compare", label: "Compare", icon: "⇔" },
  { to: "/alerts", label: "Alerts", icon: "🔔" },
  { to: "/settings", label: "Settings", icon: "⚙" },
];

export default function Layout({ children }: { children: ReactNode }) {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [aiDrawerOpen, setAiDrawerOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh" }}>
      {/* Sidebar */}
      <aside
        style={{
          width: collapsed ? 56 : 220,
          background: "var(--surface)",
          borderRight: "1px solid var(--border)",
          display: "flex",
          flexDirection: "column",
          transition: "width 0.2s",
          flexShrink: 0,
          overflow: "hidden",
        }}
      >
        {/* Brand */}
        <div
          style={{
            padding: "16px",
            borderBottom: "1px solid var(--border)",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span style={{ fontSize: 20, fontWeight: 700, color: "var(--primary-light)" }}>
            B
          </span>
          {!collapsed && (
            <span style={{ fontSize: 16, fontWeight: 600, letterSpacing: 1 }}>
              BAZAAR
            </span>
          )}
        </div>

        {/* Nav items */}
        <nav style={{ flex: 1, padding: "8px 0", overflowY: "auto" }}>
          {NAV_ITEMS.map((item) => {
            const isActive = item.to === "/"
              ? location.pathname === "/"
              : location.pathname.startsWith(item.to);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 16px",
                  textDecoration: "none",
                  color: isActive ? "var(--primary-light)" : "var(--text-muted)",
                  background: isActive ? "rgba(108, 92, 231, 0.1)" : "transparent",
                  borderRight: isActive ? "3px solid var(--primary-light)" : "3px solid transparent",
                  fontSize: 14,
                  fontWeight: isActive ? 600 : 400,
                  whiteSpace: "nowrap",
                  transition: "all 0.15s",
                }}
              >
                <span style={{ fontSize: 16, width: 20, textAlign: "center" }}>{item.icon}</span>
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            );
          })}
        </nav>

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          style={{
            padding: "12px",
            borderTop: "1px solid var(--border)",
            background: "transparent",
            border: "none",
            color: "var(--text-muted)",
            cursor: "pointer",
            fontSize: 12,
            textAlign: "center",
          }}
        >
          {collapsed ? "▸" : "◂ Collapse"}
        </button>
      </aside>

      {/* Main area */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Top bar */}
        <header
          style={{
            height: 48,
            background: "var(--surface)",
            borderBottom: "1px solid var(--border)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 16px",
            flexShrink: 0,
          }}
        >
          {/* Left: Market strip */}
          <div style={{ flex: 1 }}>
            <MarketStrip />
          </div>

          {/* Right: Status + Mode + User */}
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <button
              onClick={() => setAiDrawerOpen(true)}
              title="AI Thinking"
              style={{
                background: "transparent",
                border: "1px solid var(--border)",
                color: "var(--primary-light)",
                borderRadius: 4,
                padding: "4px 10px",
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              AI
            </button>
            <StatusCenter />
            <ModeToggle />
            {user && (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                  {user.email || "admin"}
                </span>
                <button
                  onClick={handleLogout}
                  style={{
                    background: "transparent",
                    border: "1px solid var(--border)",
                    color: "var(--text-muted)",
                    borderRadius: 4,
                    padding: "4px 8px",
                    fontSize: 12,
                    cursor: "pointer",
                  }}
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Page content */}
        <main style={{ flex: 1, overflow: "auto", padding: 24 }}>
          {children}
        </main>
      </div>

      {/* AI Thinking Drawer */}
      <AIThinkingDrawer open={aiDrawerOpen} onClose={() => setAiDrawerOpen(false)} />
    </div>
  );
}
